import { getDonorIds } from "@blis/shared/models/subject";
import {
  captureSentryException,
  captureSentryMessage,
} from "@blis/telemetry/client";
import {
  type LabelTemplateV2,
  renderTemplateFromJson,
  type ZebraDpi,
} from "@gammapeak/labels";

import { format } from "../utils/date";
import { print } from "./browser-print";
import {
  customFieldsToVariables,
  extractLabelFields,
  type LayoutDefinition,
  renderLayoutToZPL,
} from "./layout";
import { createLabelSettings, getLayoutForDPI } from "./layouts";
import type { LabelAccession, LabelSample, LabelSubject } from "./types";

type LabelType = "specimen" | "archive" | "material";

type HardwareOffsets = {
  leftMm?: number;
  topMm?: number;
};

type PrintQualitySettings = {
  printSpeed?: number;
  darkness?: number;
};

type LabelPrintOptions = HardwareOffsets & PrintQualitySettings;

function isValidDpi(dpi: number): dpi is ZebraDpi {
  return dpi === 203 || dpi === 300;
}

function normalizeDpi(dpi: number): ZebraDpi {
  if (isValidDpi(dpi)) {
    return dpi;
  }
  return 203;
}

function captureLabelRenderWarnings(
  labelType: LabelType,
  dpi: ZebraDpi,
  template: LabelTemplateV2,
  warnings: { code: string; message: string }[],
) {
  if (warnings.length === 0) {
    return;
  }

  captureSentryMessage("Label render warnings", {
    level: "warning",
    tags: {
      feature: "labels-v2",
      labelType,
      dpi: String(dpi),
      templateId: template.id,
    },
    extra: {
      warningCount: warnings.length,
      warnings,
    },
  });
}

function renderTemplateToZpl(
  labelType: LabelType,
  template: LabelTemplateV2,
  variables: Record<string, unknown>,
  dpi: number,
  options?: LabelPrintOptions,
) {
  const safeDpi = normalizeDpi(dpi);
  const renderResult = renderTemplateFromJson(template, variables, {
    dpi: safeDpi,
    textEngine: "line-safe",
    hardwareMarginLeftMm: options?.leftMm,
    hardwareMarginTopMm: options?.topMm,
    printSpeed: options?.printSpeed,
    darkness: options?.darkness,
  });

  captureLabelRenderWarnings(
    labelType,
    safeDpi,
    template,
    renderResult.warnings,
  );

  return renderResult.zpl;
}

type DonorTemplateFields = {
  caseId: string;
  unosId?: string;
  alternateId?: string;
};

function donorSubjectToTemplateFields(
  subject: LabelSubject,
): DonorTemplateFields {
  const ids = getDonorIds(subject);
  return {
    caseId: ids.caseId,
    ...(ids.unosId ? { unosId: ids.unosId } : {}),
    ...(ids.donorIds?.length ? { alternateId: ids.donorIds.join(", ") } : {}),
  };
}

function buildSpecimenTemplateVariables(
  accession: LabelAccession,
  sample: LabelSample,
  labelType: Exclude<LabelType, "material">,
): Record<string, unknown> {
  const donorFields = donorSubjectToTemplateFields(accession.subject);

  const archiveInstructions = accession.org.archiveType
    ? `Archive: ${accession.org.archiveType} ${accession.org.archiveDuration ? `for ${accession.org.archiveDuration} days` : ""}`
    : "";

  const displayId = sample.displayId ?? accession.id.toString();

  const labelFields = extractLabelFields(sample);
  const customFieldVars = customFieldsToVariables(labelFields);

  return {
    ...donorFields,
    orgName: accession.org.name,
    tubeName: sample.tube.name,
    collection: format(sample.collection) || "",
    accessionId: accession.id.toString(),
    displayId,
    barcodeContent: displayId,
    tests: sample.tests?.map((t) => `[${t.name}]`).join(" ") || "",
    ...(archiveInstructions ? { archiveInstructions } : {}),
    bottomText:
      labelType === "archive"
        ? archiveInstructions
        : sample.tests?.map((t) => `[${t.name}]`).join(" ") || "",
    ...customFieldVars,
  };
}

function buildSpecimenLegacyVariables(
  accession: LabelAccession,
  sample: LabelSample,
): Record<string, string> {
  const templateVars = buildSpecimenTemplateVariables(
    accession,
    sample,
    "specimen",
  );
  return {
    caseId: String(templateVars.caseId ?? ""),
    unosId: String(templateVars.unosId ?? ""),
    alternateId: String(templateVars.alternateId ?? ""),
    orgName: String(templateVars.orgName ?? ""),
    tubeName: String(templateVars.tubeName ?? ""),
    collection: String(templateVars.collection ?? ""),
    accessionId: String(templateVars.accessionId ?? ""),
    displayId: String(templateVars.displayId ?? ""),
    barcodeContent: String(templateVars.barcodeContent ?? ""),
    tests: String(templateVars.tests ?? ""),
    archiveInstructions: String(templateVars.archiveInstructions ?? ""),
    customField1: String(templateVars.customField1 ?? ""),
    customField2: String(templateVars.customField2 ?? ""),
    customField3: String(templateVars.customField3 ?? ""),
  };
}

/**
 * Print specimen labels using the layout engine with custom fields support
 */
export async function printSpecimenLabelsWithLayout(
  accession: LabelAccession,
  samples: LabelSample[],
  customLayout?: LayoutDefinition,
  dpi = 203,
): Promise<void> {
  try {
    const labelSettings = createLabelSettings(dpi);

    const layout = customLayout || getLayoutForDPI("specimen", dpi);
    const zplCommands: string[] = [];

    for (const sample of samples) {
      const quantity = sample.quantity || 1;
      const variables = buildSpecimenLegacyVariables(accession, sample);

      for (let i = 0; i < quantity; i++) {
        const zpl = renderLayoutToZPL(layout, labelSettings, variables, {
          printSpeed: 4,
          includeMargins: true,
        });

        zplCommands.push(zpl);
      }
    }

    if (zplCommands.length > 0) {
      await print(zplCommands.join("\n"));
    }
  } catch (err) {
    console.error("Error printing specimen labels with legacy layout", err);
    throw err;
  }
}

export async function printSpecimenLabelsWithTemplate(
  accession: LabelAccession,
  samples: LabelSample[],
  template: LabelTemplateV2,
  labelType: Exclude<LabelType, "material">,
  dpi = 203,
  options?: LabelPrintOptions,
): Promise<void> {
  try {
    const zplCommands: string[] = [];

    for (const sample of samples) {
      const quantity = sample.quantity || 1;
      const variables = buildSpecimenTemplateVariables(
        accession,
        sample,
        labelType,
      );

      for (let i = 0; i < quantity; i++) {
        zplCommands.push(
          renderTemplateToZpl(labelType, template, variables, dpi, options),
        );
      }
    }

    if (zplCommands.length > 0) {
      await print(zplCommands.join("\n"));
    }
  } catch (error) {
    captureSentryException(error, {
      tags: {
        feature: "labels-v2",
        labelType,
        dpi: String(dpi),
        templateId: template.id,
      },
    });
    throw error;
  }
}

type InoculationSample = LabelSample & {
  accessionId: number;
  accession: Pick<LabelAccession, "org" | "subject">;
};

export async function printInoculationLabels(
  samples: InoculationSample[],
  materials: { code: string; lotNumber: string; expiration: string }[],
  dpi = 300,
) {
  try {
    const labelSettings = createLabelSettings(dpi);

    const zplCommands: string[] = [];

    for (const sample of samples) {
      for (const material of materials) {
        const quantity = sample.quantity || 1;

        const displayId = sample.displayId ?? sample.accessionId.toString();

        for (let i = 0; i < quantity; i++) {
          const labelFields = extractLabelFields(sample);
          const customFieldVars = customFieldsToVariables(labelFields);

          const d = donorSubjectToTemplateFields(sample.accession.subject);
          const variables = {
            caseId: d.caseId,
            unosId: d.unosId ?? "",
            alternateId: d.alternateId ?? "",
            orgName: sample.accession.org.name,
            materialCode: material.code,
            lotNumber: material.lotNumber,
            expiration: format(material.expiration, { time: false }) || "",
            accessionId: sample.accessionId.toString(),
            displayId,
            barcodeContent: displayId,
            ...customFieldVars,
          };

          const zpl = renderLayoutToZPL(
            getLayoutForDPI("material", dpi),
            labelSettings,
            variables,
            {
              printSpeed: 4,
              includeMargins: true,
            },
          );

          zplCommands.push(zpl);
        }
      }
    }

    if (zplCommands.length > 0) {
      await print(zplCommands.join("\n"));
    }
  } catch (err) {
    console.error("Error printing specimen labels with legacy layout", err);
    throw err;
  }
}

export async function printInoculationLabelsWithTemplate(
  samples: InoculationSample[],
  materials: { code: string; lotNumber: string; expiration: string }[],
  template: LabelTemplateV2,
  dpi = 300,
  options?: LabelPrintOptions,
): Promise<void> {
  try {
    const zplCommands: string[] = [];

    for (const sample of samples) {
      for (const material of materials) {
        const quantity = sample.quantity || 1;
        const displayId = sample.displayId ?? sample.accessionId.toString();

        const labelFields = extractLabelFields(sample);
        const customFieldVars = customFieldsToVariables(labelFields);

        const variables = {
          ...donorSubjectToTemplateFields(sample.accession.subject),
          orgName: sample.accession.org.name,
          materialCode: material.code,
          lotNumber: material.lotNumber,
          expiration: format(material.expiration, { time: false }) || "",
          accessionId: sample.accessionId.toString(),
          displayId,
          barcodeContent: displayId,
          ...customFieldVars,
        };

        for (let i = 0; i < quantity; i++) {
          zplCommands.push(
            renderTemplateToZpl("material", template, variables, dpi, options),
          );
        }
      }
    }

    if (zplCommands.length > 0) {
      await print(zplCommands.join("\n"));
    }
  } catch (error) {
    captureSentryException(error, {
      tags: {
        feature: "labels-v2",
        labelType: "material",
        dpi: String(dpi),
        templateId: template.id,
      },
    });
    throw error;
  }
}

export * from "./hooks";
export * from "./layouts";
